import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que Recopilamos',
    body: 'Recopilamos únicamente la información necesaria para procesar tu compra y entregarte la guía digital, incluyendo tu nombre y dirección de correo electrónico.'
  }, {
    heading: 'Uso de la Información',
    body: 'Tu información personal se utiliza exclusivamente para procesar tu pedido, enviarte la guía adquirida y, si lo autorizas, enviarte comunicaciones relacionadas con contenido de bienestar infantil.'
  }, {
    heading: 'Protección de Datos',
    body: 'Implementamos medidas de seguridad para proteger tu información personal. No vendemos, intercambiamos ni transferimos tu información personal a terceros sin tu consentimiento.'
  }, {
    heading: 'Cookies',
    body: 'Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar algunas funcionalidades del sitio.'
  }, {
    heading: 'Tus Derechos',
    body: 'Tienes derecho a acceder, rectificar o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, puedes comunicarte con nosotros a través de los canales disponibles.'
  }, {
    heading: 'Cambios en esta Política',
    body: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios serán publicados en esta página con la fecha de última actualización.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Producto Digital',
    body: 'La Guía de Sueño Express para Bebés es un producto digital en formato PDF. Al completar la compra, recibirás acceso inmediato para descargar el material.'
  }, {
    heading: 'Uso Personal',
    body: 'El contenido de la guía es para uso personal y privado. Queda prohibida la reproducción, distribución o reventa total o parcial del material sin autorización expresa.'
  }, {
    heading: 'Propiedad Intelectual',
    body: 'Todo el contenido de la guía, incluyendo textos, gráficos, plantillas y metodología, está protegido por derechos de autor. El Método de Micro-pausas Nocturnas es propiedad intelectual de la autora.'
  }, {
    heading: 'Descargo de Responsabilidad',
    body: 'La guía ofrece orientación general sobre hábitos de sueño infantil. No sustituye el consejo médico profesional. Si tu bebé presenta problemas de salud, consulta siempre con un pediatra.'
  }, {
    heading: 'Resultados',
    body: 'Los resultados pueden variar según cada bebé y familia. Los plazos mencionados son estimaciones basadas en la experiencia general y no constituyen una garantía de resultados específicos.'
  }, {
    heading: 'Política de Reembolso',
    body: 'Debido a la naturaleza digital del producto, no se aceptan devoluciones una vez descargado el material. Te recomendamos revisar toda la información disponible antes de realizar tu compra.'
  }]
};
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const content = type === 'privacy' ? privacyContent : termsContent;
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.25
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8" onClick={onClose} role="dialog" aria-modal="true" aria-label={content.title} data-id="element-140">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-brown-dark/60 backdrop-blur-sm" data-id="element-141" />

      {/* Modal */}
      <motion.div initial={{
      opacity: 0,
      y: 30,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.97
    }} transition={{
      duration: 0.3,
      ease: [0.21, 0.47, 0.32, 0.98]
    }} className="relative bg-cream rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden border border-accent/20" onClick={e => e.stopPropagation()} data-id="element-142">
        {/* Header */}
        <div className="sticky top-0 bg-cream/95 backdrop-blur-md px-6 md:px-8 py-5 border-b border-accent/15 flex items-center justify-between z-10" data-id="element-143">
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-brown-dark" data-id="element-144">
            {content.title}
          </h2>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-white border border-cream-dark flex items-center justify-center text-brown/60 hover:text-brown-dark hover:border-accent/40 transition-colors focus:outline-none focus:ring-2 focus:ring-accent/30" aria-label="Cerrar" data-id="element-145">
            <X className="w-5 h-5" data-id="element-146" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 md:px-8 py-6 overflow-y-auto max-h-[calc(85vh-80px)]" data-id="element-147">
          <p className="text-sm text-brown/50 mb-6" data-id="element-148">
            Última actualización: Marzo 2026
          </p>
          <div className="space-y-6" data-id="element-149">
            {content.sections.map((section, i) => <div key={i} data-id="element-150">
                <h3 className="font-serif font-bold text-lg text-brown-dark mb-2" data-id="element-151">
                  {section.heading}
                </h3>
                <p className="text-brown-dark/75 leading-relaxed" data-id="element-152">
                  {section.body}
                </p>
              </div>)}
          </div>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-10 bg-cream-dark/50" data-id="element-153">
        <div className="max-w-4xl mx-auto px-6 flex flex-col items-center gap-4" data-id="element-154">
          <div className="flex items-center gap-6" data-id="element-155">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-brown/60 hover:text-brown-dark underline underline-offset-2 decoration-accent/40 hover:decoration-accent transition-colors focus:outline-none focus:ring-2 focus:ring-accent/30 rounded" data-id="element-156">
              Política de Privacidad
            </button>
            <span className="text-brown/30" data-id="element-157">·</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-brown/60 hover:text-brown-dark underline underline-offset-2 decoration-accent/40 hover:decoration-accent transition-colors focus:outline-none focus:ring-2 focus:ring-accent/30 rounded" data-id="element-158">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-brown/50 text-sm" data-id="element-159">
            © {new Date().getFullYear()} Guía de Sueño Express para Bebés.
            Todos los derechos reservados.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-160">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-161" />}
      </AnimatePresence>
    </>;
}